import { Alert, AlertTitle, Box, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseSkeleton } from "../../../lib/closet.tsx";
import StepperFormWrapper from "../../../lib/StepperFormWrapper";
import { StepperType } from "../../../lib/StepperWrapper";
import { FacingMode, getVideoStream } from "../../../lib/Stream.tsx";
import { ScreenTitle } from "../../../lib/typography";
import Video from "../../../lib/Video.tsx";

type Props = {
  stepper: StepperType;
  onSubmit: (videoOk: boolean) => void | Promise<void>;
};

enum MediaErrorTypes {
  NO_DEVICES = "no_devices",
  PERMISSION = "permission",
  GENERIC_ERROR = "error",
  NO_ERROR = "",
}

export default function TestVideoStep({ stepper, onSubmit }: Props) {
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream>();
  const [loading, setLoading] = useState<boolean>(false);
  const [mediaError, setMediaError] = useState<MediaErrorTypes>(
    MediaErrorTypes.NO_ERROR
  );
  const refVideo = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let disposed = false;

    setLoading(true);
    setMediaError(MediaErrorTypes.NO_ERROR);
    getVideoStream({
      facingMode: FacingMode.USER,
    })
      .then((sr: MediaStream) => {
        if (disposed) {
          sr.getTracks().forEach((track) => track.stop());
          return;
        }
        setStream(sr);
        setLoading(false);
        setMediaError(MediaErrorTypes.NO_ERROR);
      })
      .catch((err: unknown) => {
        if (disposed) return;
        const errorMessage = String(err);
        if (errorMessage.includes("NotAllowedError")) {
          setMediaError(MediaErrorTypes.PERMISSION);
        } else if (errorMessage.includes("NotFoundError")) {
          setMediaError(MediaErrorTypes.NO_DEVICES);
        } else {
          setMediaError(MediaErrorTypes.GENERIC_ERROR);
        }
        setLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, []);

  const stopStream = useCallback(() => {
    stream?.getTracks().forEach((track) => {
      track.stop();
    });
  }, [stream]);

  useEffect(
    () => () => {
      stopStream();
    },
    [stopStream]
  );

  return (
    <StepperFormWrapper
      stepper={stepper({})}
      handleSubmit={() => onSubmit(!mediaError)}
    >
      <ScreenTitle>{t("Check camera")}</ScreenTitle>
      <Stack
        spacing={3}
        sx={{
          marginTop: 4,
          marginBottom: 6,
        }}
      >
        <Box>{t("Can you see your video below?")}</Box>
        <Box
          sx={{
            alignSelf: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-around",
            width: "100%",
          }}
        >
          {loading && (
            <BaseSkeleton
              sx={{
                height: "50vh",
                width: "100%",
                transform: "unset",
              }}
            />
          )}
          {mediaError && (
            <Alert severity="error" sx={{ mt: 4, alignItems: "flex-start" }}>
              <AlertTitle>
                {mediaError === MediaErrorTypes.PERMISSION
                  ? t("Camera access is not allowed")
                  : mediaError === MediaErrorTypes.NO_DEVICES
                    ? t("Could not find a camera")
                    : t("Could not access the camera.")}
              </AlertTitle>
              <Typography variant="body2">
                {mediaError === MediaErrorTypes.PERMISSION
                  ? t(
                      "Please change your site permissions to allow Connect to use the camera."
                    )
                  : mediaError === MediaErrorTypes.NO_DEVICES
                    ? t("Please ensure you have a camera connected.")
                    : t("An error occurred while trying to access the camera.")}
              </Typography>
            </Alert>
          )}
          {!loading && !mediaError && stream && (
            <Video
              refVideo={refVideo}
              mediaStream={stream}
              styles={{
                transform: "rotateY(180deg)",
              }}
            />
          )}
        </Box>
      </Stack>
    </StepperFormWrapper>
  );
}
